/**
 * Streaming SSE helper for the Dwinov AI API.
 *
 * Uses fetch + ReadableStream so streaming works with cookie-based auth (unlike
 * EventSource, which can't send cookies cross-origin in all browsers). Chunks
 * arrive as `data: {json}\n\n` lines — we buffer partial frames and dispatch
 * complete JSON objects to `onEvent`.
 */

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function jsonFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = "Request failed";
    try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json();
}

// -------------------- Sessions & non-streaming ops --------------------

export const aiCreateSession = (payload) => jsonFetch("/ai/sessions", { method: "POST", body: JSON.stringify(payload) });
export const aiListSessions = (feature) => jsonFetch(`/ai/sessions${feature ? `?feature=${feature}` : ""}`);
export const aiGetSession = (id) => jsonFetch(`/ai/sessions/${id}`);
export const aiDeleteSession = (id) => jsonFetch(`/ai/sessions/${id}`, { method: "DELETE" });
export const aiFinalize = (id) => jsonFetch(`/ai/writer/${id}/finalize`, { method: "POST", body: "{}" });
export const aiRewrite = (id, payload) => jsonFetch(`/ai/writer/${id}/rewrite`, { method: "POST", body: JSON.stringify(payload) });
export const aiExport = (id, generation_id) => jsonFetch(`/ai/writer/${id}/export`, { method: "POST", body: JSON.stringify({ generation_id }) });

// Phase 2 — one-shot tools
export const aiFundingCalculator = (inputs) => jsonFetch("/ai/funding-calculator", { method: "POST", body: JSON.stringify(inputs) });
export const aiHealthScore       = (snapshot) => jsonFetch("/ai/health-score",        { method: "POST", body: JSON.stringify(snapshot) });
export const aiSuccessPredictor  = (snapshot) => jsonFetch("/ai/success-predictor",   { method: "POST", body: JSON.stringify(snapshot) });

// Phase 3
export const aiRewardGenerator = (payload) => jsonFetch("/ai/reward-generator", { method: "POST", body: JSON.stringify(payload) });
export const aiMarketing       = (payload) => jsonFetch("/ai/marketing",         { method: "POST", body: JSON.stringify(payload) });
export const aiVideoScript     = (payload) => jsonFetch("/ai/video-script",      { method: "POST", body: JSON.stringify(payload) });

// Phase 4
export const aiImage           = (payload) => jsonFetch("/ai/image",             { method: "POST", body: JSON.stringify(payload) });
export const aiAdvisor         = (payload) => jsonFetch("/ai/advisor",           { method: "POST", body: JSON.stringify(payload) });

// -------------------- Streaming turn --------------------

/**
 * Send a message and stream the response.
 *
 * @param {string} sessionId
 * @param {string} message
 * @param {(ev: {delta?: string, done?: boolean, ready_to_generate?: boolean, error?: string}) => void} onEvent
 * @param {AbortSignal?} signal
 */
export async function aiStreamMessage(sessionId, message, onEvent, signal) {
  const res = await fetch(`${API_BASE}/ai/sessions/${sessionId}/messages`, {
    method: "POST",
    credentials: "include",
    signal,
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ message }),
  });
  if (!res.ok || !res.body) {
    let detail = "Stream failed";
    try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
    throw new Error(detail);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Split on SSE frame delimiter (blank line)
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = frame.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      const payload = line.slice(6);
      try {
        const parsed = JSON.parse(payload);
        onEvent(parsed);
      } catch {
        /* malformed frame — ignore */
      }
    }
  }
}
